import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent } from "../ui/card";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { 
  Package, Fuel, AlertTriangle, CheckCircle, XCircle, 
  DollarSign, Store, TrendingUp, Wrench 
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ToolInventory = () => {
  const { user, token: authToken } = useAuth();
  
  const [tools, setTools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [userCredits, setUserCredits] = useState(0);
  const [selectedTool, setSelectedTool] = useState(null);

  useEffect(() => {
    fetchUserProfile();
    fetchInventory();
  }, []);

  const fetchUserProfile = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/member/game/profile`, {
        headers: { 'Authorization': `Bearer ${authToken}` }
      });
      const data = await response.json();
      if (data.ok) {
        setUserCredits(data.player.available_credits || 0);
      }
    } catch (error) {
      console.error('Error fetching user profile:', error);
    }
  };

  const fetchInventory = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${BACKEND_URL}/api/member/tools/inventory/mine`, {
        headers: { 'Authorization': `Bearer ${authToken}` }
      });
      const data = await response.json();
      
      if (data.ok) {
        setTools(data.tools || []);
      }
    } catch (error) {
      console.error('Error fetching inventory:', error);
      toast.error("Failed to load inventory");
    } finally {
      setLoading(false);
    }
  };

  const handleRefuel = async (ownedToolId, fuelCost) => {
    if (userCredits < fuelCost) {
      toast.error("Insufficient credits to refuel");
      return;
    }

    try {
      const response = await fetch(
        `${BACKEND_URL}/api/member/tools/inventory/${ownedToolId}/refuel`,
        {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${authToken}` }
        }
      );
      const data = await response.json();
      
      if (data.ok) {
        toast.success(data.message || "Tool refueled successfully!");
        fetchUserProfile();
        fetchInventory();
      } else {
        toast.error(data.detail || "Failed to refuel tool");
      }
    } catch (error) {
      console.error('Error refueling tool:', error);
      toast.error("Failed to refuel tool");
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      'active': 'text-green-600 bg-green-100',
      'inactive': 'text-orange-600 bg-orange-100',
      'decayed': 'text-red-600 bg-red-100',
      'rented_out': 'text-blue-600 bg-blue-100'
    };
    return colors[status] || 'text-gray-600 bg-gray-100';
  };

  const getStatusIcon = (status) => {
    const icons = {
      'active': CheckCircle,
      'inactive': AlertTriangle,
      'decayed': XCircle,
      'rented_out': DollarSign
    };
    const Icon = icons[status] || Package;
    return <Icon className="h-4 w-4" />;
  };

  const getTierBadgeColor = (tier) => {
    const colors = {
      'starter': 'bg-green-100 text-green-800',
      'professional': 'bg-blue-100 text-blue-800',
      'premium': 'bg-purple-100 text-purple-800',
      'elite': 'bg-red-100 text-red-800',
      'legendary': 'bg-yellow-100 text-yellow-800'
    };
    return colors[tier] || 'bg-gray-100 text-gray-800';
  };

  const formatNumber = (num) => {
    return new Intl.NumberFormat().format(num);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric' 
    });
  };

  const activeTools = tools.filter(t => t.owned_tool.status === 'active');
  const inactiveTools = tools.filter(t => t.owned_tool.status !== 'active');

  return (
    <div className="container mx-auto p-6">
      {/* Header */}
      <div className="mb-8">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">My Tool Inventory</h1>
            <p className="text-gray-600">Manage your marketing tools and keep them maintained</p>
          </div>
          <Button onClick={() => window.location.href = '/member/tools/store'}>
            <Store className="h-4 w-4 mr-2" />
            Visit Tool Store
          </Button>
        </div>
      </div>

      {/* Stats Banner */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
        <Card>
          <CardContent className="p-4 text-center">
            <div className="flex items-center justify-center space-x-2">
              <DollarSign className="h-5 w-5 text-green-500" />
              <span className="text-2xl font-bold text-gray-900">{formatNumber(userCredits)}</span>
            </div>
            <p className="text-sm text-gray-600 mt-1">Available Credits</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <div className="flex items-center justify-center space-x-2">
              <Package className="h-5 w-5 text-blue-500" />
              <span className="text-2xl font-bold text-gray-900">{tools.length}</span>
            </div>
            <p className="text-sm text-gray-600 mt-1">Total Tools</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <div className="flex items-center justify-center space-x-2">
              <CheckCircle className="h-5 w-5 text-green-500" />
              <span className="text-2xl font-bold text-gray-900">{activeTools.length}</span>
            </div>
            <p className="text-sm text-gray-600 mt-1">Active Tools</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <div className="flex items-center justify-center space-x-2">
              <AlertTriangle className="h-5 w-5 text-orange-500" />
              <span className="text-2xl font-bold text-gray-900">{inactiveTools.length}</span>
            </div>
            <p className="text-sm text-gray-600 mt-1">Needs Attention</p>
          </CardContent>
        </Card>
      </div>

      {/* Tools List */}
      {loading ? (
        <div className="space-y-4">
          {[...Array(3)].map((_, index) => (
            <Card key={index} className="animate-pulse">
              <CardContent className="p-6">
                <div className="h-6 bg-gray-300 rounded w-1/4 mb-4"></div>
                <div className="h-4 bg-gray-300 rounded w-3/4"></div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : tools.length === 0 ? (
        <Card className="p-12 text-center">
          <Package className="h-16 w-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-gray-900 mb-2">No tools yet</h3>
          <p className="text-gray-600 mb-4">Visit the Tool Store to purchase your first marketing tool</p>
          <Button onClick={() => window.location.href = '/member/tools/store'}>
            <Store className="h-4 w-4 mr-2" />
            Browse Tool Store
          </Button>
        </Card>
      ) : (
        <>
          {/* Active Tools Section */}
          {activeTools.length > 0 && (
            <div className="mb-8">
              <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                <CheckCircle className="h-5 w-5 text-green-500 mr-2" />
                Active Tools ({activeTools.length})
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {activeTools.map(({ owned_tool, tool_details, needs_fuel, days_until_decay }) => (
                  <Card key={owned_tool.id} className="hover:shadow-lg transition-shadow">
                    <CardContent className="p-6">
                      <div className="flex justify-between items-start mb-4">
                        <div>
                          <div className="text-4xl mb-2">{tool_details.icon}</div>
                          <h3 className="font-bold text-gray-900 mb-1">{tool_details.name}</h3>
                          <Badge className={getTierBadgeColor(tool_details.tier)}>
                            {tool_details.tier}
                          </Badge>
                        </div>
                        <Badge className={getStatusColor(owned_tool.status)}>
                          <span className="flex items-center gap-1">
                            {getStatusIcon(owned_tool.status)}
                            {owned_tool.status}
                          </span>
                        </Badge>
                      </div>

                      <div className="space-y-2 text-sm mb-4">
                        <div className="flex justify-between">
                          <span className="text-gray-600">Purchase Price:</span>
                          <span className="font-semibold">{formatNumber(owned_tool.purchase_price)} ₡</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Purchased:</span>
                          <span className="font-semibold">{formatDate(owned_tool.purchased_at)}</span>
                        </div>
                        {owned_tool.nft_serial_number && (
                          <div className="flex justify-between">
                            <span className="text-gray-600">NFT Serial:</span>
                            <span className="font-semibold text-yellow-600">#{owned_tool.nft_serial_number}</span>
                          </div>
                        )}
                        {owned_tool.total_uses > 0 && (
                          <div className="flex justify-between">
                            <span className="text-gray-600">Times Used:</span>
                            <span className="font-semibold">{owned_tool.total_uses}</span>
                          </div>
                        )}
                      </div>

                      {/* Fuel Status */}
                      {tool_details.fuel_type !== 'none' && (
                        <div className={`p-3 rounded mb-4 ${needs_fuel ? 'bg-orange-50' : 'bg-green-50'}`}>
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-sm font-semibold">
                              {needs_fuel ? '⚠️ Fuel Low' : '✓ Fuel OK'}
                            </span>
                            <Fuel className={`h-4 w-4 ${needs_fuel ? 'text-orange-600' : 'text-green-600'}`} />
                          </div>
                          {days_until_decay !== null && (
                            <p className="text-xs text-gray-600">
                              {days_until_decay > 0 
                                ? `${days_until_decay} days until decay` 
                                : 'Fuel expired'}
                            </p>
                          )}
                          <Button 
                            size="sm" 
                            className="w-full mt-2"
                            variant={needs_fuel ? "default" : "outline"}
                            onClick={() => handleRefuel(owned_tool.id, tool_details.fuel_cost)}
                          >
                            <Fuel className="h-3 w-3 mr-1" />
                            Refuel ({formatNumber(tool_details.fuel_cost)} ₡)
                          </Button>
                        </div>
                      )}

                      {/* Tool Effects */}
                      <div className="space-y-1 text-xs">
                        {tool_details.quality_boost > 0 && (
                          <div className="flex justify-between">
                            <span className="text-gray-600">Quality:</span>
                            <span className="font-semibold text-green-600">+{(tool_details.quality_boost * 100).toFixed(0)}%</span>
                          </div>
                        )}
                        {tool_details.quantity_boost > 0 && (
                          <div className="flex justify-between">
                            <span className="text-gray-600">Quantity:</span>
                            <span className="font-semibold text-blue-600">+{(tool_details.quantity_boost * 100).toFixed(0)}%</span>
                          </div>
                        )}
                        {tool_details.conversion_boost > 0 && (
                          <div className="flex justify-between">
                            <span className="text-gray-600">Conversion:</span>
                            <span className="font-semibold text-purple-600">+{(tool_details.conversion_boost * 100).toFixed(0)}%</span>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}

          {/* Inactive Tools Section */}
          {inactiveTools.length > 0 && (
            <div>
              <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                <AlertTriangle className="h-5 w-5 text-orange-500 mr-2" />
                Needs Attention ({inactiveTools.length})
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {inactiveTools.map(({ owned_tool, tool_details, needs_fuel, days_until_decay }) => (
                  <Card key={owned_tool.id} className="border-orange-200 hover:shadow-lg transition-shadow">
                    <CardContent className="p-6">
                      <div className="flex justify-between items-start mb-4">
                        <div>
                          <div className="text-4xl mb-2 opacity-50">{tool_details.icon}</div>
                          <h3 className="font-bold text-gray-900 mb-1">{tool_details.name}</h3>
                          <Badge className={getTierBadgeColor(tool_details.tier)}>
                            {tool_details.tier}
                          </Badge>
                        </div>
                        <Badge className={getStatusColor(owned_tool.status)}>
                          <span className="flex items-center gap-1">
                            {getStatusIcon(owned_tool.status)}
                            {owned_tool.status}
                          </span>
                        </Badge>
                      </div>

                      <div className="p-3 bg-orange-50 rounded mb-4">
                        <p className="text-sm font-semibold text-orange-800 mb-2">
                          {owned_tool.status === 'inactive' ? '⚠️ Tool Needs Fuel' : '❌ Tool Decayed'}
                        </p>
                        <p className="text-xs text-gray-600 mb-3">
                          {owned_tool.status === 'inactive' 
                            ? 'Refuel this tool to activate it and start using its benefits.'
                            : 'This tool has decayed and needs repair. Contact support for restoration.'}
                        </p>
                        {owned_tool.status === 'inactive' && (
                          <Button 
                            size="sm" 
                            className="w-full"
                            onClick={() => handleRefuel(owned_tool.id, tool_details.fuel_cost)}
                          >
                            <Wrench className="h-3 w-3 mr-1" />
                            Refuel Now ({formatNumber(tool_details.fuel_cost)} ₡)
                          </Button>
                        )}
                      </div>

                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-gray-600">Purchase Price:</span>
                          <span className="font-semibold">{formatNumber(owned_tool.purchase_price)} ₡</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Last Fueled:</span>
                          <span className="font-semibold">{formatDate(owned_tool.last_fueled_at)}</span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ToolInventory;
